import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { calculateDB, calculateGrowth } from "./calculations"

/**
 * Retrieves the ADM revenue comparison for a specific year and ADM number.
 *
 * @param {Object} options - The options for retrieving the revenue comparison.
 * @param {number} options.year - The year for which to retrieve the revenue comparison. If not provided, the current year will be used.
 * @param {string} options.admNumber - The ADM number for which to retrieve the revenue comparison.
 * @returns {Promise<Object>} A promise that resolves to an object containing the revenue comparison data for the current year and the last year.
 * @throws {Error} If data retrieval for the revenue comparison fails.
 */
export const getAdmRevenueComparison = async ({ year, admNumber }) => {
  const currentYear = year && year > 0 ? year : new Date().getFullYear()
  const lastYear = currentYear - 1
  const fields = ["year", "revenue", "margin"]

  const currentYearVariables = { admNumber, year: currentYear }
  const lastYearVariables = { admNumber, year: lastYear }

  const graphiQLQueryObject = [
    {
      method: "currentYearRevenue: admStatsYtd",
      variables: currentYearVariables,
      fields,
    },
    {
      method: "lastYearRevenue: admStatsYtd",
      variables: lastYearVariables,
      fields,
    },
    {
      method: "comparisonYearRevenue: admStatsYtd",
      variables: { admNumber, year: lastYear - 1 },
      fields,
    },
    {
      method: "currentYearTarget: admSummary",
      variables: currentYearVariables,
      fields: ["revenueTarget"],
    },
    {
      method: "lastYearTarget: admSummary",
      variables: lastYearVariables,
      fields: ["revenueTarget"],
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
  const queryResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data

  if (!queryResponse) throw new Error("Data retrieval for stats projection failed")

  const { currentYearRevenue, lastYearRevenue, comparisonYearRevenue, currentYearTarget, lastYearTarget } =
    queryResponse

  const defaultData = {
    revenue: null,
    revenueTarget: null,
    yearDB: null,
    revenueGrowth: null,
    dbGrowth: null,
  }

  // if one of the values is missing (from debugging value year 2021)
  if (!currentYearRevenue) {
    return {
      admNumber,
      // return null in this case
      currentYearRevenue: defaultData,
      lastYearRevenue: defaultData,
    }
  }

  // did not know the correct name in english... calculation based on the comment
  // https://reply.atlassian.net/browse/EINZA-842?focusedCommentId=47112
  const currentYearDB = calculateDB(Number(currentYearRevenue?.margin), Number(currentYearRevenue?.revenue))

  // in case only last year value is missing, still return current year data
  if (!lastYearRevenue) {
    return {
      admNumber,
      currentYearRevenue: {
        ...currentYearRevenue,
        ...currentYearTarget,
        yearDB: currentYearDB,
        // no way to calculate this, return null
        revenueGrowth: null,
        dbGrowth: null,
      },
      // no way to calculate this, show default object with null values
      lastYearRevenue: defaultData,
    }
  }

  const lastYearDB = calculateDB(Number(lastYearRevenue?.margin), Number(lastYearRevenue?.revenue))

  // going to far, could lead to null being returned, use 0 in this case
  // still having it null, should not prevent us from calculating current and last years
  const comparisonYearDB = calculateDB(
    Number(comparisonYearRevenue?.margin || 0),
    Number(comparisonYearRevenue?.revenue || 0),
  )

  const currentYearRevenueGrowth = calculateGrowth(
    Number(currentYearRevenue?.revenue),
    Number(lastYearRevenue?.revenue),
  )

  const lastYearRevenueGrowth = calculateGrowth(
    Number(lastYearRevenue?.revenue),
    Number(comparisonYearRevenue?.revenue || 0),
  )

  const currentYearDBGrowth = calculateGrowth(currentYearDB, lastYearDB)
  const lastYearDBGrowth = calculateGrowth(lastYearDB, comparisonYearDB || 0)

  const currentYearData = {
    ...currentYearRevenue,
    ...currentYearTarget,
    yearDB: currentYearDB,
    revenueGrowth: currentYearRevenueGrowth,
    dbGrowth: currentYearDBGrowth,
  }
  const lastYearData = {
    ...lastYearRevenue,
    ...lastYearTarget,
    yearDB: lastYearDB,
    revenueGrowth: lastYearRevenueGrowth,
    dbGrowth: lastYearDBGrowth,
  }

  return {
    currentYearRevenue: currentYearData,
    lastYearRevenue: lastYearData,
  }
}
